import { BookOpenCheck, Check, Layers, TriangleAlert, Undo2 } from "lucide-react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import useTranslations from "@/shared/lang/useTranslations";

import type { LedgerOperationView } from "../types/paymentsLedger.types";

export interface LedgerInvariantStripProps {
  view: LedgerOperationView;
}

/**
 * The four numbers that decide whether an operation's accounting can be trusted: how many entries it posted,
 * how many legs across how many accounts, whether every currency balances, and how many entries were
 * reversed.
 *
 * The balance tile is the point of the strip. It re-computes, client-side and in exact decimal arithmetic,
 * the very invariant the runtime asserts before writing (`assertBalanced`) — so agreement is a real
 * verification and not a restatement of what the API said.
 */
export function LedgerInvariantStrip({ view }: LedgerInvariantStripProps) {
  const { t, activeLanguage } = useTranslations("paymentsLedger");

  const accountCount = new Set(view.legs.map((leg) => leg.account_id)).size;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <ColocaKpiCard
        label={t("kpi_entries_label")}
        value={view.entries.length}
        icon={BookOpenCheck}
        accent="primary"
        meta={t("kpi_entries_meta")}
      />
      <ColocaKpiCard
        label={t("kpi_legs_label")}
        value={view.legs.length}
        icon={Layers}
        accent="info"
        meta={t("kpi_legs_meta", { accounts: accountCount })}
      />
      <ColocaKpiCard
        label={t("kpi_balance_label")}
        value={view.isBalanced ? t("kpi_balance_ok") : t("kpi_balance_broken")}
        icon={view.isBalanced ? Check : TriangleAlert}
        accent={view.isBalanced ? "success" : "danger"}
        meta={
          view.totals.length > 0 ? (
            <span className="flex flex-col gap-0.5">
              {view.totals.map((total) => (
                <span key={total.currency} className="inline-flex items-baseline gap-1.5">
                  <span className="font-mono text-[11px]">{total.currency}</span>
                  <ColocaAmountDisplay
                    value={total.debit}
                    size="xs"
                    locale={activeLanguage}
                    minimumFractionDigits={2}
                    maximumFractionDigits={8}
                  />
                  <span aria-hidden="true">=</span>
                  <ColocaAmountDisplay
                    value={total.credit}
                    size="xs"
                    locale={activeLanguage}
                    minimumFractionDigits={2}
                    maximumFractionDigits={8}
                  />
                </span>
              ))}
            </span>
          ) : (
            t("kpi_balance_meta")
          )
        }
      />
      <ColocaKpiCard
        label={t("kpi_reversals_label")}
        value={view.reversalCount}
        icon={Undo2}
        accent={view.reversalCount > 0 ? "warning" : "neutral"}
        meta={view.reversalCount > 0 ? t("kpi_reversals_meta") : t("kpi_reversals_none")}
      />
    </div>
  );
}
